import { useHistory } from 'react-router-dom';

import { trackEvent } from 'shared/lib/analytics/trackEvent';
import { useAuth } from 'shared/lib/auth/useAuth';
import { urls } from 'shared/urls';

import { UpgradePromoEvent } from './constants';
import { usePromoStore } from './usePromoStore';
import type { UpgradeDecision } from './useUpgradeTrigger';

export type UpgradePromoActions = {
  onCta: () => void;
  onDismiss: () => void;
  onMaybeLater: () => void;
  onViewAllPlans: () => void;
};

/**
 * Shared handler set for all upgrade-promo surfaces (banner / modal / toast).
 * Owns the trackEvent + store + navigation wiring so each surface component
 * only forwards the four callbacks. Returns `null` when the user isn't
 * eligible; callers can short-circuit rendering on that signal.
 */
export const useUpgradePromoActions = (decision: UpgradeDecision): UpgradePromoActions | null => {
  const { user } = useAuth();
  const recordDismissal = usePromoStore((state) => state.recordDismissal);
  const recordDemotion = usePromoStore((state) => state.recordDemotion);
  const history = useHistory();

  if (!decision.eligible) return null;

  const { layout, pitch } = decision;
  const isCompanyIndividual = !!user?.isCompanyIndividual;
  // Individuals don't have an existing subscription to change — route them
  // through the Choose-your-plan setup flow instead (CL-6382).
  const changePlanUrl = isCompanyIndividual ? urls.subscriptionFlow.url() : urls.subscriptionChangePlan.url();

  const track = (event: UpgradePromoEvent) => trackEvent(event, { layout, target_tier: pitch.targetTier });

  return {
    onCta: () => {
      track(UpgradePromoEvent.CTA_CLICK);
      recordDismissal();
      history.push(changePlanUrl);
    },
    onDismiss: () => {
      track(UpgradePromoEvent.DISMISSED);
      recordDismissal();
    },
    onMaybeLater: () => {
      track(UpgradePromoEvent.DEMOTED_TO_BANNER);
      // Individuals never see a banner — demotion would leave the modal
      // mounted. Treat as a soft dismiss so `INDIVIDUAL_REPROMPT_DAYS` kicks
      // in and the modal closes.
      if (isCompanyIndividual) {
        recordDismissal();
      } else {
        recordDemotion();
      }
    },
    onViewAllPlans: () => {
      track(UpgradePromoEvent.VIEW_ALL_PLANS);
      recordDismissal();
      history.push(changePlanUrl);
    },
  };
};
